import { useTranslation } from 'react-i18next';
import { restore as restoreAuth } from '@/store/auth/auth.slice';
import {
  setBookingOrder as setBO,
  setBookingId as setBI,
  setOrderInfo as setOF,
  setStartBookingDate as setSBD,
  setStartDate as setSD,
  setCustomer as setC,
  restore,
} from './booking.slice';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import useBoletiaConfig from '@/hooks/useBoletiaConfig';
import orderBy from 'lodash/orderBy';

export default function useBooking() {
  const { t } = useTranslation();
  const { eventUseNewCommision } = useBoletiaConfig();
  const dispatch = useDispatch();

  const { preferences: { currency = '', fee_breakdown = 't' } = {} } =
    useSelector((state) => state.event || {});

  const booking = useSelector((state) => state.booking);
  const event = useSelector((state) => state.event);
  const promotions = useSelector((state) => state.promotions);
  const summary = useSelector((state) => state.summary, shallowEqual);
  const addOnsActive = useSelector((state) => state.addons.active);
  const ticketInsuranceStore = useSelector(
    (state) => state.addons.ticketInsurance,
  );

  const ticketsSelected = useSelector((state) =>
    state.ticketsSelected.ids.map((id) => state.ticketsSelected.entities[id]),
  );

  const setCustomer = (questions) => {
    dispatch(setC(questions));
  };

  const setStartBookingDate = () => {
    dispatch(setSBD());
  };

  const setStartDate = () => {
    dispatch(setSD());
  };

  const setBookingOrder = (order) => {
    dispatch(setBO(order));
  };

  const setBookingId = (order) => {
    dispatch(setBI(order));
  };

  const setOrderInfo = (orderInfo) => {
    dispatch(setOF(orderInfo));
  };

  const getZone = (ticket) => {
    if (ticket.seatsIoInfo) {
      const labels = ticket.seatsIoInfo;
      if (labels.parent) {
        return t('checkout.home.cart.seatSelected', {
          parent: labels.parent,
          own: labels.own,
        });
      } else {
        return t('checkout.home.cart.seatQuantity', {
          quantity: ticket.quantity,
          own: labels.own,
        });
      }
    }
    return t('checkout.home.cart.ticketsQuantity', {
      quantity: ticket.quantity,
    });
  };

  const getTicketCategory = (ticketTypeId) => {
    const ticket = ticketsSelected.find(
      (item) => item.ticket_type_id === ticketTypeId,
    );
    if (ticket) {
      return ticket.category;
    }
    return null;
  };

  const getTicketZone = (ticket) => {
    return ticket.section
      ? `${ticket.section} - ${
          ticket.row ? ' Fila ' + ticket.row + ' - ' : ''
        } ${ticket.seat ? 'Asiento ' + ticket.seat : ''}`
      : 'General';
  };

  const getTicketInfo = (tickets) => {
    const ticketsInfo = [];

    const useSeats = event.event.use_seats;

    tickets.forEach((t) => {
      const zoneData = {
        section: t.seat_section,
        row: t.row_number,
        seat: t.seat_number,
      };
      if (ticketsInfo.some((ticket) => ticket.id === t.ticket_type_id)) {
        const founded = ticketsInfo.find(
          (ticket) => ticket.id === t.ticket_type_id,
        );

        founded.qty += 1;
        if (useSeats && zoneData.row)
          founded.zones.push(getTicketZone(zoneData));
      } else {
        ticketsInfo.push({
          id: t.ticket_type_id,
          price: t.price,
          name: t.ticket_type_name,
          qty: 1,
          zones: useSeats && zoneData.row ? [getTicketZone(zoneData)] : [],
        });
      }
    });
    return ticketsInfo;
  };

  const getResume = () => {
    let subtotal = 0;
    let totalTickets = 0;
    let subtotalTickets = 0;
    let totalProcessorFee = 0;
    let totalServiceFee = 0;
    let totalTransactionFee = 0;
    let discount = 0;
    let ticketFee = 0;
    let ticketsResumeDetail = [];
    let ticketsDetail = [];

    if (booking.orderInfo && booking.orderInfo.tickets) {
      totalTickets = booking.orderInfo.real_tickets;

      ticketsDetail = getTicketInfo(booking.orderInfo.tickets);
      ticketsResumeDetail = orderBy(booking.orderInfo.tickets, ['id']).map(
        (ticket) => {
          const category = getTicketCategory(ticket.ticket_type_id);
          subtotal += ticket.price;
          return {
            id: ticket.id,
            name: ticket.ticket_type_name,
            color: category ? category.color : null,
            price: ticket.price,
            total: ticket.price,
            quantity: 1,
            zone: ticket.seat_section
              ? `${ticket.seat_section} - ${
                  ticket.row_number ? 'Fila ' + ticket.row_number + ' - ' : ''
                } ${ticket.seat_number ? 'Asiento ' + ticket.seat_number : ''}`
              : 'General',
          };
        },
      );
    } else {
      ticketsResumeDetail = ticketsSelected.map((ticket) => {
        const ticketTypeTotal = ticket.price * ticket.quantity;
        subtotal += ticketTypeTotal;
        totalTickets += ticket.quantity;
        return {
          id: ticket.id,
          name: ticket.name,
          color: ticket.category.color,
          price: ticket.price,
          total: ticketTypeTotal,
          quantity: ticket.quantity,
          zone: getZone(ticket),
        };
      });
    }
    let charge = 0;
    let total = subtotal + charge - discount;
    let ticketInsurance = null;

    const getValue = (value) => {
      return fee_breakdown === 'f' ? 0 : value;
    };

    if (summary) {
      subtotal = summary.subtotal;
      discount = summary.discount;
      charge = fee_breakdown === 'f' ? 0 : summary.charges_per_service;
      total = summary.total_to_pay;
      subtotalTickets = summary.total_tickets;
      totalProcessorFee = getValue(summary.total_processor_fee);
      totalServiceFee = getValue(summary.total_service_fee);
      totalTransactionFee = getValue(summary.total_transaction_fee);
      if (
        addOnsActive &&
        ticketInsuranceStore.selected &&
        Array.isArray(summary.addon_available)
      ) {
        const ticketInsuranceData = summary.addon_available.find(
          (addon) => addon.kind === 'ticket_insurance',
        );
        if (ticketInsuranceData) {
          ticketInsurance = ticketInsuranceData.price;
        }
      }
    }

    return {
      ticketsResumeDetail,
      ticketsDetail,
      subtotal,
      charge,
      subtotalTickets,
      totalProcessorFee,
      totalTransactionFee,
      totalServiceFee,
      discount,
      total,
      hasTickets: ticketsResumeDetail.length > 0,
      currency,
      totalTickets,
      promotion: promotions ? promotions.code : null,
      commissions: eventUseNewCommision(event?.event.id, fee_breakdown)
        ? summary.commissions
        : null,
      ticketInsurance,
    };
  };

  const resetBooking = () => {
    dispatch(restore());
    dispatch(restoreAuth());
  };

  return {
    resume: getResume(),
    booking,
    setBookingOrder,
    setBookingId,
    setCustomer,
    setOrderInfo,
    resetBooking,
    setStartBookingDate,
    setStartDate,
  };
}
